import { useCallback, useEffect, useMemo, useState } from 'react';
import { HelpPanel, type HelpContent } from '../../portfolio_definition/HelpPanel';
import { pmGet, pmPost } from '../api';
import './AuditTrail.css';

/**
 * The Audit Trail — §12.
 *
 * *"What changed since last week?"*, which the client expressed as *"Initiative 1003's Milestone
 * 1102 due date changed from Aug 20 to Dec 20."*
 *
 * That sentence is the whole design. A row here is ONE FIELD, ON ONE RECORD, WITH A BEFORE AND
 * AN AFTER — not "record X was saved". A save that changes progress and status is two facts, and
 * the reader may care about only one of them. Everything else follows: the lenses group those
 * facts four ways, and the fields chart counts updates only, because Created and Deleted concern
 * a whole record and carry no field.
 *
 * ── GROUP HEADERS CARRY NO COUNTS, AND THAT IS DELIBERATE ──────────────────────────────
 *
 * Per-group tallies like "9 Updated" were removed. On a screen whose entire content is changes,
 * a running count on every header is noise, and the totals that matter are already in the
 * statistical cards. Portfolio Exceptions keeps its severity chips because there the counts ARE
 * the point — how bad is this owner's backlog. Here they are not.
 *
 * ── THE PERIOD RELOADS, IT DOES NOT RE-FILTER ──────────────────────────────────────────
 *
 * The period decides what the SERVER fetches. Filtering a fetched set client-side would make
 * "Everything held" mean "everything in the last 31 days", which is the wrong answer told
 * confidently.
 */

interface AuditRow {
  id: string;
  ts: string;
  who: string;
  email: string;
  screenKey: string;
  screenLabel: string;
  tbl: string;
  rec: string;
  recLabel: string;
  action: 'Created' | 'Updated' | 'Deleted';
  field: string;
  fieldLabel: string;
  before: string | null;
  after: string | null;
  pid: string | null;
  opp: string | null;
  pj: string | null;
}

type Lens = 'timeline' | 'person' | 'record' | 'screen' | 'dash';

/** §12.7 — Today · Last 7 · Last 14 · Last 21 · Last month · Everything held. */
const PERIODS: { d: number; label: string }[] = [
  { d: 1, label: 'Today' },
  { d: 7, label: 'Last 7 days' },
  { d: 14, label: 'Last 14 days' },
  { d: 21, label: 'Last 21 days' },
  { d: 31, label: 'Last month' },
  { d: 365, label: 'Everything held' },
];

function BarChart({ data, color }: { data: Record<string, number>; color: string }) {
  const labels = Object.keys(data);
  if (!labels.length) return <div className="cempty">—</div>;
  const max = Math.max(1, ...labels.map((l) => data[l] ?? 0));
  return (
    <div>
      {labels.map((l) => (
        <div className="cbar-row" key={l}>
          <div className="cbar-lbl" title={l}>
            {l}
          </div>
          <div className="cbar-track">
            <div
              className="cbar-fill"
              style={{ width: `${((data[l] ?? 0) / max) * 100}%`, background: color }}
            />
          </div>
          <div className="cbar-val">{data[l]}</div>
        </div>
      ))}
    </div>
  );
}

function Doughnut({ slices }: { slices: { k: string; v: number; c: string }[] }) {
  const total = slices.reduce((a, x) => a + x.v, 0);
  if (!total) return <div className="cempty">—</div>;
  return (
    <div>
      <div className="cdough-strip">
        {slices.map((x) => (
          <div
            className="cdough-seg"
            key={x.k}
            style={{ width: `${(x.v / total) * 100}%`, background: x.c }}
            title={`${x.k}: ${x.v}`}
          />
        ))}
      </div>
      <div className="cdough-legend">
        {slices.map((x) => (
          <span className="cdough-key" key={x.k}>
            <span className="cdough-box" style={{ background: x.c }} />
            {x.k} <span className="cdough-n">{x.v}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/** Biggest first, capped at 10 — a chart with 40 labels is unreadable. */
function countBy(rows: AuditRow[], fn: (r: AuditRow) => string): Record<string, number> {
  const m: Record<string, number> = {};
  rows.forEach((r) => {
    const k = fn(r) || '—';
    m[k] = (m[k] ?? 0) + 1;
  });
  return Object.keys(m)
    .sort((a, b) => (m[b] ?? 0) - (m[a] ?? 0))
    .slice(0, 10)
    .reduce<Record<string, number>>((o, k) => {
      o[k] = m[k] ?? 0;
      return o;
    }, {});
}

const AUDIT_HELP: HelpContent = {
  title: 'Audit Trail',
  icon: '🕘',
  purpose:
    'Every change made to programme data, with <strong>what it was before and what it is now</strong>. This is the screen that answers &ldquo;what changed since last week?&rdquo; &mdash; and, when something looks wrong, who changed it and from which screen.',
  doHere: [
    'Choose a <strong>period</strong> &mdash; today, the last week or three, a month, or everything held. The period decides what is fetched, so changing it reloads rather than re-filtering.',
    'Read the <strong>statistical cards</strong>; click <strong>Created</strong>, <strong>Updated</strong> or <strong>Deleted</strong> to narrow the list to that kind of change, and click again to clear.',
    'Switch how the list is grouped: <strong>Timeline</strong> (by day), <strong>By person</strong>, <strong>By record</strong>, or <strong>By screen</strong>.',
    'Open the <strong>Dashboard</strong> tab for the same picture drawn &mdash; changes per day, the created/updated/deleted split, who changed most, where changes happen, which fields change, and the most-edited records.',
  ],
  fits: 'Any execution screen &rarr; <strong>Audit Trail</strong> &rarr; the record that changed',
  firstTime:
    'Leave the period on <strong>Last 7 days</strong> and read the <strong>Timeline</strong> top to bottom. It is the shortest description of what the programme did this week.',
  sections: [
    {
      label: 'What a row means',
      type: 'html',
      html: 'A row is <strong>one field, on one record, with a before and an after</strong>. It is not &ldquo;this record was saved&rdquo;.<br><br>So a save that changes two fields makes <strong>two rows</strong> &mdash; because you may care about only one of them, and burying it inside a single &ldquo;record updated&rdquo; entry would hide it.<br><br><strong>Created</strong> and <strong>Deleted</strong> carry no field: they concern the whole record rather than one column. That is why the <em>which fields change</em> chart counts <strong>updates only</strong> &mdash; including the others would invent a field called nothing.',
    },
    {
      label: 'Retention is manual, and why',
      type: 'html',
      html: '<strong>Nothing expires automatically.</strong> An audit trail that quietly deletes itself is worse than none, because you cannot tell an empty period from a purged one &mdash; and the moment you cannot tell, the record stops being evidence of anything.<br><br>Entries older than <strong>90 days</strong> can be removed, but only by an administrator and only deliberately. <strong>The purge records itself</strong> in this same log, so a gap in the history always has an explanation sitting next to it.',
    },
    {
      label: 'What you see is what you may see',
      type: 'html',
      html: 'Entries tied to a delivery project are filtered to the <strong>projects you have access to</strong>. Entries with no project &mdash; settings changes, project creation, the purge itself &mdash; are <strong>programme-wide and administrator-only</strong>.<br><br>Two people opening this screen can legitimately see different totals. The numbers are per-viewer, not global.',
    },
  ],
  links: [
    { label: 'Execution Control Center →', href: '/project_management/program-management/delivery-execution/execution-control-center' },
    { label: 'Portfolio Exceptions →', href: '/project_management/portfolio/portfolio-oversight/portfolio-exceptions' },
  ],
};

export function AuditTrail() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [purge, setPurge] = useState({ days: 90, count: 0, from: null as string | null, can: false });
  const [capped, setCapped] = useState(false);
  const [maxRows, setMaxRows] = useState(20000);

  const [lens, setLens] = useState<Lens>('timeline');
  const [action, setAction] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, 1>>({});
  const [allOpen, setAllOpen] = useState(true);

  /* Measured height — `100%` resolves against nothing in this shell (CLAUDE.md). */
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);
  useEffect(() => {
    if (!rootEl) return;
    const main = rootEl.closest('main');
    const measure = () => {
      const top = rootEl.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
      setRootH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [rootEl]);

  /* THE PERIOD RELOADS FROM THE SERVER. It is not a client-side filter. */
  const load = useCallback((d: number) => {
    setLoading(true);
    pmGet<{
      success: boolean;
      rows: AuditRow[];
      purgeDays: number;
      purgeable: number;
      purgeFrom: string | null;
      canPurge: boolean;
      capped: boolean;
      maxRows: number;
      error?: string;
    }>(`/audit-trail?days=${d}`)
      .then((r) => {
        if (!r.success) throw new Error(r.error ?? 'failed');
        setRows(r.rows ?? []);
        setPurge({ days: r.purgeDays, count: r.purgeable, from: r.purgeFrom, can: r.canPurge });
        setCapped(r.capped);
        setMaxRows(r.maxRows);
        setError(null);
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(days);
  }, [days, load]);

  const visible = useMemo(
    () => (action ? rows.filter((r) => r.action === action) : rows),
    [rows, action],
  );

  const stats = useMemo(() => {
    const n = (a: string) => rows.filter((r) => r.action === a).length;
    return {
      total: rows.length,
      created: n('Created'),
      updated: n('Updated'),
      deleted: n('Deleted'),
      records: new Set(rows.map((r) => `${r.tbl}|${r.rec}`)).size,
      people: new Set(rows.map((r) => r.who)).size,
    };
  }, [rows]);

  /**
   * §12.10 — the grouping keys read `recLabel` and `screenLabel`, which is exactly where the
   * client/server field-name drift bites: the route must alias to THESE names. It does, quoted,
   * and the group-count test below the component is what proves it rather than reading the code.
   */
  const groups = useMemo(() => {
    const m = new Map<string, { key: string; sub: string; rows: AuditRow[] }>();
    visible.forEach((r) => {
      let key: string;
      let sub: string;
      if (lens === 'person') {
        key = r.who;
        sub = r.email;
      } else if (lens === 'record') {
        key = r.recLabel;
        sub = [r.tbl, r.rec, r.pj].filter(Boolean).join(' · ');
      } else if (lens === 'screen') {
        key = r.screenLabel || '—';
        sub = '';
      } else {
        key = r.ts.slice(0, 10);
        sub = '';
      }
      const id = lens === 'record' ? `${r.tbl}|${r.rec}` : key;
      const g = m.get(id) ?? { key, sub, rows: [] };
      g.rows.push(r);
      m.set(id, g);
    });
    const out = [...m.entries()].map(([id, g]) => ({ id, ...g }));
    // Timeline reads newest day first; the other lenses read busiest first.
    return lens === 'timeline'
      ? out.sort((a, b) => b.key.localeCompare(a.key))
      : out.sort((a, b) => b.rows.length - a.rows.length || a.key.localeCompare(b.key));
  }, [visible, lens]);

  const doPurge = async () => {
    if (
      !window.confirm(
        `Permanently delete ${purge.count} audit entries older than ${purge.days} days?\n\n` +
          `The oldest is from ${purge.from}. This cannot be undone; the purge itself is recorded in the log.`,
      )
    )
      return;
    try {
      const r = await pmPost<{ success: boolean; removed: number; error?: string }>(
        '/audit-trail/purge',
        {},
      );
      if (!r.success) throw new Error(r.error ?? 'failed');
      setNotice(`${r.removed} entries removed. The purge is recorded below.`);
      load(days);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const val = (v: string | null) => (v === null ? <span className="at-none">empty</span> : v);

  const table = (rs: AuditRow[]) => (
    <div className="at-tw">
      <table className="at-tbl">
        <colgroup>
          <col style={{ width: 108 }} />
          <col style={{ width: 92 }} />
          <col style={{ width: 220 }} />
          <col style={{ width: 150 }} />
          <col />
          <col style={{ width: 150 }} />
        </colgroup>
        <thead>
          <tr>
            <th>When</th>
            <th>Action</th>
            <th>Record</th>
            <th>Field</th>
            <th>Change</th>
            <th>Who / where</th>
          </tr>
        </thead>
        <tbody>
          {rs.map((r) => (
            <tr key={r.id}>
              <td className="at-when">{r.ts.replace('T', ' ')}</td>
              <td>
                <span className={`at-act ${r.action}`}>{r.action}</span>
              </td>
              <td>
                <div className="at-rec">
                  {r.recLabel}
                  <small>
                    {r.tbl} · {r.rec}
                    {r.pj ? ` · ${r.pj}` : ''}
                  </small>
                </div>
              </td>
              <td className="at-fld">{r.action === 'Updated' ? r.fieldLabel : '—'}</td>
              <td>
                {r.action === 'Updated' ? (
                  <div className="at-val">
                    <span className="at-b">{val(r.before)}</span>
                    <span className="at-arrow">→</span>
                    <span className="at-a">{val(r.after)}</span>
                  </div>
                ) : r.action === 'Created' ? (
                  <span className="at-a">{val(r.after)}</span>
                ) : (
                  <span className="at-b">{val(r.before)}</span>
                )}
              </td>
              <td>
                <div className="at-rec">
                  {r.who}
                  <small>{r.screenLabel}</small>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const dash = () => {
    const rs = visible;
    const perDay: Record<string, number> = {};
    rs.forEach((r) => {
      const d = r.ts.slice(0, 10);
      perDay[d] = (perDay[d] ?? 0) + 1;
    });
    const days14 = Object.keys(perDay)
      .sort()
      .slice(-14)
      .reduce<Record<string, number>>((o, k) => {
        o[k] = perDay[k]!;
        return o;
      }, {});
    /* UPDATES ONLY — Created and Deleted carry no field, so counting them would invent one. */
    const updates = rs.filter((r) => r.action === 'Updated');
    return (
      <div className="at-charts">
        <div className="at-chart">
          <h4>Changes per day</h4>
          <div className="box">
            <BarChart data={days14} color="#1f3864" />
          </div>
        </div>
        <div className="at-chart">
          <h4>Created / Updated / Deleted</h4>
          <div className="box">
            <Doughnut
              slices={[
                { k: 'Created', v: rs.filter((r) => r.action === 'Created').length, c: '#2e6b3e' },
                { k: 'Updated', v: rs.filter((r) => r.action === 'Updated').length, c: '#1565c0' },
                { k: 'Deleted', v: rs.filter((r) => r.action === 'Deleted').length, c: '#b3261e' },
              ]}
            />
          </div>
        </div>
        <div className="at-chart">
          <h4>Who changed most</h4>
          <div className="box">
            <BarChart data={countBy(rs, (r) => r.who)} color="#1565c0" />
          </div>
        </div>
        <div className="at-chart">
          <h4>Where changes happen</h4>
          <div className="box">
            <BarChart data={countBy(rs, (r) => r.screenLabel || '—')} color="#b26a00" />
          </div>
        </div>
        <div className="at-chart">
          <h4>Which fields change</h4>
          <div className="box">
            <BarChart data={countBy(updates, (r) => r.fieldLabel)} color="#2e6b3e" />
          </div>
        </div>
        <div className="at-chart">
          <h4>Most-edited records</h4>
          <div className="box">
            <BarChart data={countBy(rs, (r) => r.recLabel)} color="#5c6b7a" />
          </div>
        </div>
      </div>
    );
  };

  const card = (
    key: string | null,
    colour: string,
    value: number,
    label: string,
    caption: string,
  ) =>
    key ? (
      <button
        type="button"
        className="at-stat"
        key={label}
        style={{ '--c': colour } as React.CSSProperties}
        aria-pressed={action === key}
        onClick={() => setAction((p) => (p === key ? null : key))}
      >
        <span className="lb">{label}</span>
        <span className="vl">{value}</span>
        <span className="cp">{caption}</span>
      </button>
    ) : (
      <div className="at-stat static" key={label} style={{ '--c': colour } as React.CSSProperties}>
        <span className="lb">{label}</span>
        <span className="vl">{value}</span>
        <span className="cp">{caption}</span>
      </div>
    );

  return (
    <div className="at-page" ref={setRootEl} style={rootH ? { height: rootH } : undefined}>
      {/* §13.1 — the app's screen-heading framework, not one invented here. */}
      <div className="at-head">
        <div className="at-title-row">
          <i className="material-icons">history</i>
          <div className="at-titles">
            <h1 className="page-header__title">Audit Trail</h1>
            <p>Every change to programme data, with what it was before.</p>
          </div>
          <HelpPanel help={AUDIT_HELP} />
        </div>

        <div className="at-toolbar">
          <label htmlFor="at-days">Period</label>
          <select
            id="at-days"
            className="at-select"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
          >
            {PERIODS.map((p) => (
              <option key={p.d} value={p.d}>
                {p.label}
              </option>
            ))}
          </select>

          {/*
            §12.8 — a destructive control is a BUTTON, it carries the count so the consequence is
            legible before the click, and when there is nothing to do it is REPLACED BY A
            SENTENCE rather than disabled. A disabled button here was visually identical to a
            label: it was clicked, nothing happened, and the feature was reported as missing.
          */}
          {purge.can && purge.count > 0 ? (
            <button
              type="button"
              className="at-purge"
              style={{ marginLeft: 'auto' }}
              onClick={() => void doPurge()}
            >
              <i className="material-icons">delete_sweep</i>
              Purge {purge.count} entries older than {purge.days} days
            </button>
          ) : purge.can ? (
            <span className="at-purge-none">
              Nothing older than {purge.days} days to purge
            </span>
          ) : null}
        </div>

        <div className="at-stats">
          {card(null, '#1c2733', stats.total, 'Changes', 'in this period')}
          {card('Created', '#2e6b3e', stats.created, 'Created', 'new records')}
          {card('Updated', '#1565c0', stats.updated, 'Updated', 'field changes')}
          {card('Deleted', '#b3261e', stats.deleted, 'Deleted', 'records removed')}
          {card(null, '#1c2733', stats.records, 'Records touched', 'distinct records')}
          {card(null, '#1c2733', stats.people, 'People', 'made these changes')}
        </div>

        <div className="at-tabs" role="tablist" aria-label="Group by">
          {(
            [
              ['timeline', 'Timeline'],
              ['person', 'By person'],
              ['record', 'By record'],
              ['screen', 'By screen'],
              ['dash', 'Dashboard'],
            ] as [Lens, string][]
          ).map(([k, label]) => (
            <button
              type="button"
              className={`at-tab${lens === k ? ' on' : ''}`}
              data-label={label}
              key={k}
              onClick={() => {
                setLens(k);
                setOpen({});
                setAllOpen(true);
              }}
            >
              <span>{label}</span>
            </button>
          ))}
          <span className="at-tools">
            <span className="at-cnt">
              <b>{visible.length}</b> {action ? action.toLowerCase() : 'changes'}
            </span>
            {action ? (
              <button type="button" className="at-lnk" onClick={() => setAction(null)}>
                Clear filter
              </button>
            ) : null}
            {lens === 'dash' ? null : (
              <button
                type="button"
                className="at-lnk"
                onClick={() => {
                  setAllOpen((p) => !p);
                  setOpen({});
                }}
              >
                {allOpen ? 'Collapse all' : 'Expand all'}
              </button>
            )}
          </span>
        </div>
      </div>

      <div className="at-body">
        {error ? <div className="at-warn">{error}</div> : null}
        {notice ? <div className="at-warn">{notice}</div> : null}
        {capped ? (
          <div className="at-warn">
            Showing the most recent {maxRows.toLocaleString()} entries for this period — choose a
            shorter period to see all of them.
          </div>
        ) : null}

        {loading ? (
          <div className="at-empty">Loading…</div>
        ) : lens === 'dash' ? (
          dash()
        ) : !visible.length ? (
          <div className="at-empty">
            {action
              ? `Nothing was ${action.toLowerCase()} in this period.`
              : 'No changes recorded in this period.'}
          </div>
        ) : (
          groups.map((g) => {
            const o = allOpen || !!open[g.id];
            return (
              <div className={`at-grp${o ? ' open' : ''}`} key={g.id}>
                {/* §12.7 — NO COUNTS ON THE HEADER. It names the bucket, and nothing else. */}
                <button
                  type="button"
                  className="at-gh"
                  aria-expanded={o}
                  onClick={() => {
                    setAllOpen(false);
                    setOpen((p) => {
                      const n = { ...p };
                      if (n[g.id]) delete n[g.id];
                      else n[g.id] = 1;
                      return n;
                    });
                  }}
                >
                  <span className="at-car">▶</span>
                  <span className="at-who">
                    <span className="n">{g.key}</span>
                    {g.sub ? <span className="r">{g.sub}</span> : null}
                  </span>
                </button>
                <div className="at-gb">{table(g.rows)}</div>
              </div>
            );
          })
        )}

        <p className="at-note">
          A row is one field on one record, with what it was and what it is now — so a save that
          changed two fields appears as two rows. <strong>Created</strong> and{' '}
          <strong>Deleted</strong> concern the whole record and carry no field. Nothing expires
          automatically; entries older than {purge.days} days can be removed by an administrator,
          and that purge is itself recorded here. Entries are filtered to the delivery projects
          you have access to.
        </p>
      </div>
    </div>
  );
}
